import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { movimientosService, type FiltrosMovimientos } from '@/services/movimientosService'
import { honorariosService } from '@/services/honorariosService'
import { useNotificacionesModulo } from '@/core/hooks/useNotificacionesModulo'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { PLANTILLAS_NOTIFICACION } from '@/core/types/notificaciones'
import { useAuditor } from '@/hooks/useAuditor'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import { balanceForwardKeys } from '@/hooks/useBalanceForward'
import { reporteFinancieroKeys } from '@/hooks/useReporteFinanciero'
import { reportesKeys } from '@/hooks/useReportes'
import { cuentaCorrienteKeys } from '@/hooks/useCuentaCorriente'
import type { AplicacionPago } from '@/services/aplicacionesService'
import type { Database } from '@/types/database'

type MovimientoInsert = Database['public']['Tables']['movimientos']['Insert']
type MovimientoUpdate = Database['public']['Tables']['movimientos']['Update']

// Query keys
export const movimientosKeys = {
  all: ['movimientos'] as const,
  lists: () => [...movimientosKeys.all, 'list'] as const,
  list: (organizacionId: string | null, filtros?: FiltrosMovimientos) =>
    [...movimientosKeys.lists(), organizacionId, filtros] as const,
  details: () => [...movimientosKeys.all, 'detail'] as const,
  detail: (id: string) => [...movimientosKeys.details(), id] as const,
  porCliente: (clienteId: string, organizacionId: string | null, fechaDesde?: string, fechaHasta?: string) =>
    [...movimientosKeys.all, 'cliente', clienteId, organizacionId, fechaDesde, fechaHasta] as const,
  porClienteBase: (clienteId: string) => [...movimientosKeys.all, 'cliente', clienteId] as const,
  cuentas: () => ['cuentas'] as const,
  clientesConMovimiento: (mesAnio: string, organizacionId: string | null) =>
    [...movimientosKeys.all, 'clientesConMovimiento', mesAnio, organizacionId] as const,
}

const invalidateMovimientoCliente = (queryClient: QueryClient, clienteId: string) => {
  queryClient.invalidateQueries({ queryKey: movimientosKeys.porClienteBase(clienteId) })
  queryClient.invalidateQueries({ queryKey: balanceForwardKeys.porCliente(clienteId) })
  // Resumen de la ficha: antigüedad de deuda y último pago salen de los movimientos.
  queryClient.invalidateQueries({ queryKey: cuentaCorrienteKeys.cliente(clienteId) })
}

const invalidateMovimientoRelated = (
  queryClient: QueryClient,
  clienteId?: string | null,
) => {
  queryClient.invalidateQueries({ queryKey: movimientosKeys.lists() })
  queryClient.invalidateQueries({ queryKey: ['clientes'] })
  queryClient.invalidateQueries({ queryKey: reporteFinancieroKeys.all })
  queryClient.invalidateQueries({ queryKey: reportesKeys.all })
  // vista_saldos_cuentas suma los movimientos de cada cuenta.
  queryClient.invalidateQueries({ queryKey: movimientosKeys.cuentas() })
  if (clienteId) invalidateMovimientoCliente(queryClient, clienteId)
}

// Hook para listar movimientos con filtros
export function useMovimientos(filtros?: FiltrosMovimientos) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: movimientosKeys.list(organizacionId, filtros),
    // refetch() corre la queryFn aunque enabled sea false.
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return movimientosService.listar(supabase!, organizacionId, filtros)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

// Hook para obtener un movimiento por ID
export function useMovimiento(id: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: movimientosKeys.detail(id),
    queryFn: () => movimientosService.obtenerPorId(supabase!, id),
    enabled: !!supabase && !supabaseLoading && !!id,
  })
}

// Hook para obtener movimientos de un cliente (usa listar() para enrichment completo)
// Cuenta corriente de un cliente: sin verMovimientos o sin organización la query no corre.
export function useMovimientosCliente(clienteId: string, fechaDesde?: string, fechaHasta?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()

  return useQuery({
    queryKey: movimientosKeys.porCliente(clienteId, organizacionId, fechaDesde, fechaHasta),
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return movimientosService.listar(supabase!, organizacionId, { clienteId, fechaDesde, fechaHasta })
    },
    enabled:
      !!supabase && !supabaseLoading && !!clienteId && !!organizacionId && puede.verMovimientos === true,
  })
}

// Hook para listar cuentas
export function useCuentas() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: [...movimientosKeys.cuentas(), organizacionId] as const,
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return movimientosService.listarCuentas(supabase!, organizacionId)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Notificación "pago registrado" (ORR-57 Fase 2, caso Rodrigo): todos los miembros
 * activos de la org se enteran, excepto el actor.
 *
 * Vive fuera del hook de creación porque desde ORR-260 hay DOS caminos que crean un
 * pago —el INSERT genérico y la RPC transaccional— y duplicar el bloque haría que la
 * notificación se pierda en silencio en uno de los dos. Best-effort: un fallo acá
 * nunca revierte un pago ya confirmado.
 */
function useNotificarPagoRegistrado() {
  const { user } = useAuth0()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { notificar, destinatariosOrg, nombreCliente } = useNotificacionesModulo()

  return async (pago: {
    tipo: string
    concepto: string
    cliente_id?: string | null
    monto?: number | null
  }) => {
    // pago.monto es NOT NULL en el schema (source of truth del movimiento).
    // debe/haber son para double-entry accounting y pueden ser null.
    if (pago.tipo !== 'Pago' || !organizacionId || !supabase || !pago.cliente_id || pago.monto == null) return

    try {
      const clienteNombre = await nombreCliente(pago.cliente_id)
      const actor = user?.name || user?.email || 'Alguien'
      // El aviso lleva cliente e importe: solo lo recibe quien puede ver movimientos.
      const destinatarios = await destinatariosOrg(organizacionId, undefined, 'verMovimientos')
      if (destinatarios.length === 0) return

      const plantilla = PLANTILLAS_NOTIFICACION.pagoRegistrado(
        clienteNombre,
        pago.monto,
        actor,
        pago.concepto
      )
      await notificar({
        ...plantilla,
        destinatarios,
        enlace: `/movimientos?cliente=${pago.cliente_id}`,
      })
    } catch (err) {
      console.error('[notificaciones] error notificando pago registrado:', err)
    }
  }
}

// Hook para crear movimiento
export function useCrearMovimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { crear } = useAuditor()
  const { puede } = usePermissions()
  const notificarPagoRegistrado = useNotificarPagoRegistrado()

  return useMutation({
    mutationFn: (movimiento: Omit<MovimientoInsert, 'organizacion_id'>) => {
      assertPermiso(puede, 'crearMovimiento')
      if (!organizacionId) throw new Error('Sin organización activa')
      return movimientosService.crear(supabase!, {
        ...movimiento,
        organizacion_id: organizacionId,
      })
    },
    onSuccess: async (data) => {
      invalidateMovimientoRelated(queryClient, data.cliente_id)
      crear('movimiento', data.id, {
        tipo: data.tipo,
        concepto: data.concepto,
        debe: data.debe,
        haber: data.haber,
        cliente_id: data.cliente_id,
      })
      await notificarPagoRegistrado(data)
    },
  })
}

/**
 * Registra un pago y lo aplica a sus deudas en una sola transacción (ORR-260).
 *
 * Reemplaza al par `useCrearMovimiento` + aplicar el pago a sus deudas, que corría en dos
 * requests: cuando la segunda fallaba —típicamente porque el diálogo tenía saldos
 * viejos— el pago quedaba grabado sin aplicar a nada, y repetir el clic lo duplicaba.
 * Con `aplicaciones` vacío registra un pago a cuenta.
 */
export function useRegistrarPagoConAplicaciones() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { crear } = useAuditor()
  const { puede } = usePermissions()
  const notificarPagoRegistrado = useNotificarPagoRegistrado()

  return useMutation({
    mutationFn: async (params: {
      clienteId: string
      monto: number
      fecha: string
      concepto: string
      cuentaId: string
      servicioId?: string | null
      mesAnio?: string | null
      observaciones?: string | null
      aplicaciones: AplicacionPago[]
    }) => {
      assertPermiso(puede, 'crearMovimiento')
      if (!organizacionId) throw new Error('Sin organización activa')

      const pagoId = await movimientosService.registrarPagoConAplicaciones(supabase!, {
        ...params,
        organizacionId,
        createdBy: user?.email ?? 'sistema',
      })

      return { id: pagoId, ...params }
    },
    onSuccess: async (data) => {
      invalidateMovimientoRelated(queryClient, data.clienteId)
      crear('movimiento', data.id, {
        tipo: 'Pago',
        concepto: data.concepto,
        debe: 0,
        haber: data.monto,
        cliente_id: data.clienteId,
      })
      await notificarPagoRegistrado({
        tipo: 'Pago',
        concepto: data.concepto,
        cliente_id: data.clienteId,
        monto: data.monto,
      })
    },
  })
}

// Hook para actualizar movimiento
export function useActualizarMovimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()
  const { puede } = usePermissions()
  const { notificar, responsablesCliente, nombreCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MovimientoUpdate }) => {
      assertPermiso(puede, 'editarMovimiento')
      return movimientosService.actualizar(supabase!, id, data)
    },
    onSuccess: async (data, variables) => {
      invalidateMovimientoRelated(queryClient, data.cliente_id)
      queryClient.invalidateQueries({ queryKey: movimientosKeys.detail(variables.id) })
      editar('movimiento', variables.id, { cambios: variables.data })

      // Notif #4 Fase 2: deuda vencida
      // Dispara cuando el movimiento cambia a estado='vencido'. Notifica a los
      // responsables del cliente (con fallback a admins/owner si no hay).
      if (variables.data.estado === 'vencido' && data.cliente_id && supabase && data.monto != null) {
        try {
          const clienteNombre = await nombreCliente(data.cliente_id)
          const monto = data.monto
          const destinatarios = await responsablesCliente(data.cliente_id, 'verMovimientos')
          if (destinatarios.length > 0) {
            const plantilla = PLANTILLAS_NOTIFICACION.deudaVencida(clienteNombre, monto, data.concepto)
            await notificar({
              ...plantilla,
              destinatarios,
              enlace: `/movimientos?cliente=${data.cliente_id}`,
            })
          }
        } catch (err) {
          console.error('[notificaciones] error notificando deuda vencida:', err)
        }
      }
    },
  })
}

// Hook para eliminar movimiento
export function useEliminarMovimiento() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { eliminar } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: async (id: string) => {
      assertPermiso(puede, 'eliminarMovimiento')
      const movimiento = await movimientosService.obtenerPorId(supabase!, id)
      // ORR-218: la RPC limpia server-side el comprobante adjunto; el service borra su archivo de Storage.
      await movimientosService.eliminar(supabase!, id)
      return movimiento
    },
    onSuccess: (data) => {
      invalidateMovimientoRelated(queryClient, data.cliente_id)
      eliminar('movimiento', data.id, {
        tipo: data.tipo,
        concepto: data.concepto,
        debe: data.debe,
        haber: data.haber,
        cliente_id: data.cliente_id,
        fecha: data.fecha,
      })
    },
  })
}

// Hook para crear movimientos masivos
export function useCrearMovimientosMasivo() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { generarLote } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (movimientos: Array<Omit<MovimientoInsert, 'organizacion_id'>>) => {
      assertPermiso(puede, 'crearMovimiento')
      if (!organizacionId) throw new Error('Sin organización activa')
      return movimientosService.crearMasivo(
        supabase!,
        movimientos.map(m => ({ ...m, organizacion_id: organizacionId }))
      )
    },
    onSuccess: (data) => {
      invalidateMovimientoRelated(queryClient, null)
      // Invalidate per-client caches for each affected client
      const clienteIds = new Set(data.map(m => m.cliente_id).filter(Boolean))
      clienteIds.forEach(id => {
        if (id) invalidateMovimientoCliente(queryClient, id)
      })
      generarLote({ cantidad: data.length, tipo: 'creacion_masiva' })
    },
  })
}

// Hook para obtener clientes con honorarios (servicios configurados)
// Trae el precio de cada servicio: sin verHonorariosCliente la query no corre.
export function useClientesConHonorarios() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { puede, permisosListos } = usePermissions()

  return useQuery({
    queryKey: ['clientes', 'conHonorarios'],
    queryFn: () => honorariosService.obtenerResumenTodos(supabase!),
    enabled: !!supabase && !supabaseLoading && permisosListos === true && puede.verHonorariosCliente === true,
    staleTime: 1000 * 60,
  })
}

// Hook para obtener los clientes que ya tienen deuda mensual en un período
export function useClientesConMovimiento(mesAnio: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: movimientosKeys.clientesConMovimiento(mesAnio, organizacionId),
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return movimientosService.obtenerClientesConMovimiento(supabase!, organizacionId, mesAnio)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId && !!mesAnio,
  })
}

// Hook para generar movimientos en lote (usando honorariosService)
export function useGenerarMovimientosEnLote() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { generarLote } = useAuditor()
  const { puede } = usePermissions()
  const { notificar, destinatariosOrg } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({
      mesAnio,
      concepto,
      fecha,
      clienteIds,
      loteCompleto,
    }: {
      mesAnio: string
      concepto: string
      fecha: string
      clienteIds?: string[]
      /** Si el lote se generó para todos los clientes activos. El aviso de cuota nueva se ancla en esta marca. */
      loteCompleto?: boolean
    }) => {
      assertPermiso(puede, 'crearMovimiento')
      // La deuda se calcula con el precio de cada servicio.
      assertPermiso(puede, 'verHonorariosCliente')
      // Generar para un cliente desde su cuenta corriente no pide generarLoteMovimientos.
      if (loteCompleto) assertPermiso(puede, 'generarLoteMovimientos')
      return honorariosService.generarMovimientosEnLote(supabase!, mesAnio, concepto, fecha, organizacionId!, clienteIds)
    },
    onSuccess: async (data, variables) => {
      invalidateMovimientoRelated(queryClient, null)
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // Invalidate per-client caches for affected clients
      if (variables.clienteIds) {
        variables.clienteIds.forEach(id => invalidateMovimientoCliente(queryClient, id))
      }
      generarLote({
        mes_anio: variables.mesAnio,
        concepto: variables.concepto,
        creados: data.creados,
        omitidos: data.omitidos,
        total: data.total,
        monto_total: data.montoTotal,
        ...(variables.loteCompleto !== undefined && { lote_completo: variables.loteCompleto }),
      })

      // Notif #2 Fase 2: lote de honorarios generado
      // Notifica a TODOS los miembros de la org (excepto el actor) cuando se
      // genera un lote. Reemplaza el auto-notificación bug anterior que
      // notificaba solo al propio usuario con notificarSistema.
      if (data.creados > 0 && organizacionId) {
        try {
          const actor = user?.name || user?.email || 'Alguien'
          // El aviso lleva cliente e importe: solo lo recibe quien puede ver movimientos.
          const destinatarios = await destinatariosOrg(organizacionId, undefined, 'verMovimientos')
          if (destinatarios.length > 0) {
            const plantilla = PLANTILLAS_NOTIFICACION.loteHonorariosGenerado(
              variables.mesAnio,
              data.creados,
              data.omitidos,
              data.montoTotal,
              actor
            )
            await notificar({
              ...plantilla,
              destinatarios,
              enlace: '/movimientos',
            })
          }
        } catch (err) {
          console.error('[notificaciones] error notificando lote generado:', err)
        }
      }
    },
  })
}

// Hook para previsualizar generación de movimientos
export function usePrevisualizarGeneracion() {
  const { supabase } = useSupabase()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: ({
      mesAnio,
      clienteIds,
    }: {
      mesAnio: string
      clienteIds?: string[]
    }) => {
      assertPermiso(puede, 'verHonorariosCliente')
      return honorariosService.previsualizarGeneracion(supabase!, mesAnio, clienteIds)
    },
  })
}

// Previsualización de la deuda mensual de un solo cliente. Cuelga de porClienteBase para que
// cualquier movimiento nuevo del cliente la invalide.
export function usePrevisualizacionCliente(clienteId: string, mesAnio: string, habilitado = true) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: [...movimientosKeys.porClienteBase(clienteId), 'previsualizacion', mesAnio] as const,
    queryFn: async () => {
      const detalle = await honorariosService.previsualizarGeneracion(supabase!, mesAnio, [clienteId])
      return detalle.find(d => d.cliente_id === clienteId) ?? null
    },
    enabled: habilitado && !!supabase && !supabaseLoading && !!clienteId,
  })
}
